'use client';

import {
  useId,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ComponentProps,
} from 'react';
import { styled, keyframes } from 'styled-components';
import cs from 'classnames';
import Script from 'next/script';
import * as Sentry from '@sentry/nextjs';

import {
  CLIENT_EXPECTED_REJECTIONS,
  blogSubscribeTags,
} from '@lib/blog-subscribe-telemetry';
import { pushToDataLayer } from '@lib/gtm';
import { visuallyHiddenStyles } from '@lib/styles';
import {
  BLOG_SUBSCRIBE_FORM_ID,
  BLOG_SUBSCRIBE_RECAPTCHA_ACTION,
} from '@lib/constants';
import { CLIENT_ENV } from '@lib/env';
import {
  buttonForegroundStyles,
  buttonHoverStyles,
} from '@dvd/components/Button';

const RECAPTCHA_SITE_KEY = CLIENT_ENV.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

/**
 * Whether reCAPTCHA is active. Exported so Subscribe can gate the reCAPTCHA
 * branding it renders alongside this form: Google only permits hiding the badge
 * when that branding is shown, and the branding is only accurate when the
 * script below actually loads.
 */
export const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY);

type HiddenField = {
  objectTypeId?: string;
  name: string;
  value: string;
};

type BlogSubscribeFormProps = {
  className?: string;
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const formId = BLOG_SUBSCRIBE_FORM_ID;
const placeholder = 'Email address';
const submitText = 'Subscribe';
const successMessage =
  'Signed, sealed, delivered! More video content awaits you.';
const utmParamNames = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[0.75]};
  width: 100%;

  ${({ theme }) => theme.mq.minWidth.medium} {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`;

const VisuallyHiddenLabel = styled.label`
  ${visuallyHiddenStyles}
`;

const EmailInput = styled.input`
  appearance: none;
  background-color: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.blue800};
  border-radius: ${({ theme }) => theme.radius.btn};
  // Input field background is always white so we need to override the global color token and force it to blue800
  color: ${({ theme }) => theme.colors.blue800};
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  padding-block: ${({ theme }) => theme.spacing[0.5]};
  padding-inline: ${({ theme }) => theme.spacing[1.5]};
  width: 100%;
  max-width: unset;

  // Magic number so that the input field doesn't expand too much on larger viewports and the it's more in line with the design in Figma.
  ${({ theme }) => theme.mq.minWidth.medium} {
    max-width: 55%;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
    padding-block: ${({ theme }) => theme.spacing[1]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey500};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.blue500};
    outline: none;
  }

  // orange700, the error color used on white pages, does not clear 3:1
  // against this module's dark blue container. Use the lighter orange300 so
  // the invalid border stays visible and matches the message below the field.
  &[aria-invalid='true'] {
    border-color: ${({ theme }) => theme.colors.orange300};
  }
`;

/**
 * Bespoke submit button rather than the shared `Button` component. `Button`
 * renders a bare string child with no loading or disabled state, so it can't
 * express the label/spinner swap or the reCAPTCHA-pending disabled styling this
 * form needs, and its background is always `fit-content` whereas the form drives
 * its own responsive widths. The shared visuals are reused via
 * `buttonForegroundStyles`/`buttonHoverStyles` so the two stay in sync.
 */
const SubmitButtonBackground = styled.button`
  background-color: ${({ theme }) => theme.colors.blue500};
  border-radius: ${({ theme }) => theme.radius.btn};
  border-width: 0;
  cursor: pointer;
  display: inline-block;
  margin: 0;
  padding: 0;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    width: fit-content;
  }
`;

const SubmitButtonForeground = styled.span`
  --button-shadow-color: ${({ theme }) => theme.colors.blue500};
  ${buttonForegroundStyles}
  background-color: ${({ theme }) => theme.colors.blue500};
  border: 3px solid ${({ theme }) => theme.colors.blue500};
  font-size: ${({ theme }) => theme.typography.fontSize.headingFive};
  outline: 0.05em solid ${({ theme }) => theme.colors.blue500};
  padding-block: ${({ theme }) => theme.spacing[0.75]};
  padding-inline: ${({ theme }) => theme.spacing[1.25]};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.headingFive};
  }

  ${SubmitButtonBackground}:hover:not(:disabled) & {
    ${buttonHoverStyles}
  }

  ${SubmitButtonBackground}:active:not(:disabled) & {
    top: 2px;
  }
`;

const ButtonLabel = styled.span`
  display: inline-block;

  ${SubmitButtonForeground}.submitting & {
    visibility: hidden;
  }
`;

const Spinner = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1em;
  height: 1em;
  /* em (not rem) so the size and the centering offset both track the button's
     font-size, which grows at the large breakpoint. The offset must stay half
     the spinner's size for the negative-margin centering to hold; rem would
     lock the offset to the root font-size and mis-center it. */
  margin-block-start: -0.5em;
  margin-inline-start: -0.5em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

const FeedbackMessage = styled.p`
  color: inherit;
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  margin-block-start: ${({ theme }) => theme.spacing[1]};
  margin-block-end: 0;

  // This module renders on its own dark blue container no matter which
  // scheme the enclosing Section uses, so its error color is fixed too:
  // orange300 clears 3:1 here where orange700 does not.
  &.error {
    color: ${({ theme }) => theme.colors.orange300};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

/**
 * Carries the proxy's HTTP status alongside the message so the caller can tell
 * the failures apart. Without the status every rejection is just an `Error` with
 * a generic string, and a 429 (the rate limiter doing its job) reports to Sentry
 * identically to a 502 (HubSpot is down) — which buries a real outage inside
 * expected noise. The status is not privileged information: the browser already
 * received it.
 */
class SubmitError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SubmitError';
    this.status = status;
  }
}

/**
 * Submits the subscribe form to HubSpot through our own `/api/hubspot/submit`
 * proxy route rather than posting to HubSpot directly. The proxy verifies the
 * reCAPTCHA token, enforces rate limiting, and keeps the portal id / secrets
 * server-side (see `src/app/api/hubspot/submit/route.ts`).
 *
 * Builds the HubSpot Forms v3 payload from the email and any UTM hidden fields,
 * and attaches submission `context` (page URL/title plus the HubSpot tracking
 * cookie) so HubSpot can attribute the lead to the visitor's session.
 *
 * @returns the parsed JSON body from the proxy on success.
 * @throws {SubmitError} when the proxy responds non-OK; the thrown message is
 *   the proxy's `error` field when present so the caller can surface or log it.
 */
async function submitToHubSpot(
  formId: string,
  email: string,
  hiddenFields: HiddenField[],
  recaptchaToken: string,
) {
  // '0-1' is HubSpot's object-type id for the Contact object; every field on a
  // subscribe submission targets the contact record.
  const fields = [
    { objectTypeId: '0-1', name: 'email', value: email },
    ...hiddenFields.map((field) => ({
      objectTypeId: field.objectTypeId ?? '0-1',
      name: field.name,
      value: field.value,
    })),
  ];

  const context: Record<string, string> = {
    pageUri: window.location.href,
    pageName: document.title,
  };

  // `hubspotutk` is HubSpot's first-party tracking cookie. Forwarding it lets
  // HubSpot tie this submission to the visitor's existing analytics session; it
  // may be absent (e.g. cookies blocked), so it's only added when present.
  const hutk = document.cookie
    .split('; ')
    .find((row) => row.startsWith('hubspotutk='))
    ?.split('=')[1];
  if (hutk) {
    context.hutk = hutk;
  }

  const response = await fetch('/api/hubspot/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ formId, fields, context, recaptchaToken }),
  });

  // The proxy returns a JSON `{ error }` body on failure; fall back to a generic
  // message if it can't be parsed. The caller maps any throw to the form's
  // error state.
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new SubmitError(error?.error ?? 'Submission failed', response.status);
  }

  return response.json();
}

const BlogSubscribeForm = ({ className }: BlogSubscribeFormProps) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);
  const [recaptchaArmed, setRecaptchaArmed] = useState(false);
  const emailId = useId();
  const errorId = useId();
  const successRef = useRef<HTMLParagraphElement>(null);

  // Submitting replaces the whole form (including the focused submit button)
  // with the success message, which would silently drop keyboard focus and
  // leave screen readers unaware anything happened. Moving focus to the
  // message restores a focus position and prompts AT to announce it.
  useEffect(() => {
    if (status === 'success') {
      successRef.current?.focus();
    }
  }, [status]);

  // next/script's onReady only means the script file has loaded and executed;
  // grecaptcha finishes initializing asynchronously after that. ready() is the
  // documented gate that queues the callback until initialization completes,
  // so readiness is only flagged once execute() is actually safe to call.
  const handleRecaptchaReady = useCallback(() => {
    window.grecaptcha?.ready(() => setRecaptchaReady(true));
  }, []);

  // The script failing to load is a total outage for this visitor and the only
  // failure the server can never see: `recaptchaReady` stays false, so the submit
  // button below stays disabled forever and no request is ever made. Ad blockers,
  // enterprise proxies, and CSP all block google.com/recaptcha for a real slice of
  // traffic, so without this capture the form is dead for those visitors while
  // Sentry stays completely silent. Reported as a message rather than an
  // exception: `onError` hands us no Error, and this is an environment condition,
  // not a code fault.
  const handleRecaptchaError = useCallback(() => {
    setRecaptchaFailed(true);
    Sentry.captureMessage('reCAPTCHA script failed to load', {
      level: 'warning',
      tags: blogSubscribeTags('recaptcha-script-failed'),
      extra: { formId },
    });
  }, []);

  // The script is fetched on first interaction with the form rather than on page
  // load. Crawlers render every blog page but never focus a field, so before this
  // gate they tripped `onError` en masse and buried the signal it exists to
  // carry — a real visitor whose ad blocker or corporate proxy blocks
  // google.com — under bot noise. Focus is the earliest reliable evidence of a
  // human intending to subscribe, and it precedes submission by the seconds it
  // takes to type an email, which is ample for the script to load. It also keeps
  // a third-party script off the page for the vast majority of readers who never
  // subscribe. `onFocus` sits on the form (React's focus event bubbles) so
  // reaching the input or the button by mouse or keyboard both arm it.
  const armRecaptcha = useCallback(() => setRecaptchaArmed(true), []);

  // When a site key is configured the server requires a valid reCAPTCHA token,
  // which can only be produced once the script is ready. Block submission while
  // it is in flight so we never send a doomed, token-less request. Gated on
  // `recaptchaArmed` so the button is not disabled before the visitor has
  // touched the form — until they do, there is no script to wait for.
  const recaptchaPending =
    RECAPTCHA_ENABLED && recaptchaArmed && !recaptchaReady;

  const handleSubmit: NonNullable<ComponentProps<'form'>['onSubmit']> = async (
    e,
  ) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Read UTM params at submit time. This handler only runs on the client,
    // so no `typeof window` guard is needed.
    const params = new URLSearchParams(window.location.search);
    const hiddenFields: HiddenField[] = utmParamNames
      .map((name) => ({ name, value: params.get(name) ?? '' }))
      .filter((field) => field.value !== '');

    try {
      let recaptchaToken = '';

      if (RECAPTCHA_SITE_KEY && recaptchaReady && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action: BLOG_SUBSCRIBE_RECAPTCHA_ACTION,
        });
      }

      await submitToHubSpot(formId, email, hiddenFields, recaptchaToken);
      setStatus('success');
      setEmail('');
      pushToDataLayer({
        event: 'storefront.hubspot.onFormSubmitted',
        formId,
      });
    } catch (error) {
      // A guard doing its job is not an incident. Report the proxy's expected
      // rejections as warnings and keep `captureException` for genuine faults —
      // 5xx, a network drop, a grecaptcha throw — so a HubSpot outage does not
      // arrive at the same level, and in the same issue, as routine bot traffic.
      // See CLIENT_EXPECTED_REJECTIONS.
      const status = error instanceof SubmitError ? error.status : undefined;
      const expected = CLIENT_EXPECTED_REJECTIONS[status];

      if (expected) {
        Sentry.captureMessage(expected.message, {
          level: 'warning',
          tags: blogSubscribeTags(expected.reason),
          extra: { formId, status },
        });
      } else {
        Sentry.captureException(error, {
          tags: blogSubscribeTags('client-submit-failed'),
          extra: { formId, status },
        });
      }

      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <FeedbackMessage
        className={className}
        role="status"
        tabIndex={-1}
        ref={successRef}
      >
        {successMessage}
      </FeedbackMessage>
    );
  }

  return (
    <div className={className}>
      {RECAPTCHA_ENABLED && recaptchaArmed && (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
          strategy="afterInteractive"
          onReady={handleRecaptchaReady}
          onError={handleRecaptchaError}
        />
      )}
      <FormContainer onSubmit={handleSubmit} onFocus={armRecaptcha}>
        <VisuallyHiddenLabel htmlFor={emailId}>
          Email address
        </VisuallyHiddenLabel>
        <EmailInput
          id={emailId}
          type="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          required
          aria-invalid={status === 'error'}
          aria-describedby={status === 'error' ? errorId : undefined}
          disabled={status === 'submitting'}
          autoComplete="email"
        />
        <SubmitButtonBackground
          type="submit"
          disabled={status === 'submitting' || recaptchaPending}
        >
          <SubmitButtonForeground
            className={cs({ submitting: status === 'submitting' })}
          >
            <ButtonLabel>{submitText}</ButtonLabel>
            {status === 'submitting' && <Spinner aria-hidden="true" />}
          </SubmitButtonForeground>
        </SubmitButtonBackground>
      </FormContainer>
      {status === 'error' && (
        <FeedbackMessage id={errorId} className="error" role="alert">
          {errorMessage}
        </FeedbackMessage>
      )}
      {recaptchaFailed && (
        <FeedbackMessage className="error" role="alert">
          We couldn’t load part of this form. Please refresh the page to try
          again.
        </FeedbackMessage>
      )}
    </div>
  );
};

export default BlogSubscribeForm;
